/**
 * Task #3408 — App Startup & Loading Experience Overhaul
 *
 * TopProgressBar: Nashid-orange thin bar at the top of the viewport that
 * advances during route transitions and lazy chunk downloads.
 *
 * State machine: idle → loading (0→72% over 800ms) → complete (72→100% over
 * 150ms) → fade-out (opacity 0 over 200ms) → idle
 *
 * Exports:
 *   - topProgressStore  — imperative singleton (start / done) for use outside React
 *   - TopProgressBar    — the visual component; mount once at the app root
 */
import { useState, useEffect, useRef, useSyncExternalStore } from "react";

type ProgressPhase = "idle" | "loading" | "complete" | "fading";

interface ProgressState {
  phase: ProgressPhase;
}

type Listener = () => void;

function createProgressStore() {
  let state: ProgressState = { phase: "idle" };
  const listeners = new Set<Listener>();
  let completeTimer: ReturnType<typeof setTimeout> | null = null;
  let fadeTimer: ReturnType<typeof setTimeout> | null = null;

  function notify() {
    listeners.forEach(l => l());
  }

  function clearTimers() {
    if (completeTimer) { clearTimeout(completeTimer); completeTimer = null; }
    if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
  }

  return {
    getSnapshot(): ProgressState {
      return state;
    },
    subscribe(fn: Listener): () => void {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    start() {
      clearTimers();
      state = { phase: "loading" };
      notify();
    },
    done() {
      if (state.phase === "idle") return;
      clearTimers();
      state = { phase: "complete" };
      notify();
      fadeTimer = setTimeout(() => {
        state = { phase: "fading" };
        notify();
        fadeTimer = setTimeout(() => {
          state = { phase: "idle" };
          notify();
        }, 220);
      }, 160);
    },
  };
}

export const topProgressStore = createProgressStore();

interface TopProgressBarProps {
  isPending?: boolean;
}

export function TopProgressBar({ isPending = false }: TopProgressBarProps) {
  const progressState = useSyncExternalStore(
    topProgressStore.subscribe,
    topProgressStore.getSnapshot,
  );
  const [widthPct, setWidthPct] = useState(0);
  const animFrameRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isPending && progressState.phase === "idle") {
      topProgressStore.start();
    }
    if (!isPending && progressState.phase === "loading") {
      topProgressStore.done();
    }
  }, [isPending, progressState.phase]);

  useEffect(() => {
    if (animFrameRef.current) {
      clearTimeout(animFrameRef.current);
      animFrameRef.current = null;
    }

    if (progressState.phase === "loading") {
      startTimeRef.current = Date.now();
      const animate = () => {
        const elapsed = Date.now() - (startTimeRef.current ?? Date.now());
        const progress = Math.min(0.72, 1 - Math.pow(1 - elapsed / 800, 3));
        setWidthPct(progress * 100);
        if (progress < 0.72) {
          animFrameRef.current = setTimeout(animate, 16);
        }
      };
      animate();
    } else if (progressState.phase === "complete") {
      setWidthPct(100);
    } else if (progressState.phase === "fading") {
      setWidthPct(100);
    } else if (progressState.phase === "idle") {
      setWidthPct(0);
    }

    return () => {
      if (animFrameRef.current) {
        clearTimeout(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [progressState.phase]);

  const isVisible = progressState.phase !== "idle";
  const isFading = progressState.phase === "fading";

  if (!isVisible) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: 3,
        zIndex: 9999,
        pointerEvents: "none",
        opacity: isFading ? 0 : 1,
        transition: isFading ? "opacity 200ms ease-out" : "none",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${widthPct}%`,
          background: "#F97316",
          borderRadius: "0 2px 2px 0",
          boxShadow: "0 0 8px rgba(249,115,22,0.6)",
          transition: progressState.phase === "complete"
            ? "width 150ms ease-in-out"
            : "none",
        }}
      />
    </div>
  );
}
